import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import StepPipeline from "@/components/StepPipeline";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { toast } from "sonner";
import { useAuth, formatApiError } from "@/context/AuthContext";
import { ROLE_LABELS, STATUS_COLORS, STATUS_DOT, ROLE_COLORS, STEPS, STEP_FIELDS, isNexusInfra, isNexusLicensing } from "@/lib/steps";
import { ArrowLeft, CheckCircle2, AlertOctagon, RotateCcw, Mail, MessageSquare, Clock, Send, Play, Building2, Calendar as CalIcon, Users, Paperclip } from "lucide-react";
import { cn, hasRole, formatTime12h } from "@/lib/utils";
import AttachmentsPanel from "@/components/AttachmentsPanel";
import NewMembersPanel from "@/components/NewMembersPanel";
import EmailCompose, { BillingCompose } from "@/components/EmailCompose";
import CheckboxGroup from "@/components/CheckboxGroup";
import MultiSelect from "@/components/MultiSelect";
import TimeInput from "@/components/TimeInput";

export default function ProgramDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [program, setProgram] = useState(null);
  const [comments, setComments] = useState([]);
  const [emails, setEmails] = useState([]);
  const [tab, setTab] = useState("workflow");
  const [pendingScrollStep, setPendingScrollStep] = useState(null);

  useEffect(() => {
    if (pendingScrollStep == null) return;
    document.getElementById(`step-${pendingScrollStep}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
    setPendingScrollStep(null);
  }, [pendingScrollStep, tab]);

  const scrollToStep = (stepNumber) => {
    setTab("workflow");
    setPendingScrollStep(stepNumber);
  };

  // dialogs
  const [editStep, setEditStep] = useState(null); // step number for edit/complete dialog
  const [blockStep, setBlockStep] = useState(null);
  const [reopenStep, setReopenStep] = useState(null);
  const [emailForStep, setEmailForStep] = useState(null); // {step_number, kind}
  const [extensionOpen, setExtensionOpen] = useState(false);
  const [extDecisionOpen, setExtDecisionOpen] = useState(false);

  const load = async () => {
    try {
      const [{ data: p }, { data: c }, { data: e }] = await Promise.all([
        api.get(`/programs/${id}`),
        api.get(`/programs/${id}/comments`),
        api.get(`/programs/${id}/emails`),
      ]);
      setProgram(p);
      setComments(c);
      setEmails(e);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  useEffect(() => { load(); }, [id]);

  if (!program) return <div className="p-10 text-sm text-muted-foreground">Loading…</div>;

  const currentStep = program.steps.find((s) => s.step_number === program.current_step);
  const canActCurrent = hasRole(user, currentStep?.owner);

  return (
    <div className="p-6 sm:p-10 xl:pr-64 max-w-[1400px] mx-auto page-enter">
      {/* Go back to the list exactly as it was (its page/filters live in the URL). If there's no
          in-app history — e.g. the program was opened from an email link — go to the Dashboard. */}
      <button onClick={() => (window.history.state?.idx > 0 ? nav(-1) : nav("/"))} className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1 mb-4" data-testid="pd-back">
        <ArrowLeft className="w-3.5 h-3.5" /> Back
      </button>

      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <div className="text-[11px] uppercase tracking-widest text-muted-foreground">{program.status.toUpperCase()} · Program</div>
          <h1 className="font-heading text-2xl font-semibold tracking-tight mt-1">{program.training_title}</h1>
          <div className="flex items-center gap-4 text-xs text-muted-foreground mt-2 flex-wrap">
            <span className="flex items-center gap-1"><Building2 className="w-3.5 h-3.5" /> {program.client_name}</span>
            <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5" /> Batch of {program.batch_size || "-"}</span>
            {program.start_date && <span className="flex items-center gap-1"><CalIcon className="w-3.5 h-3.5" /> {program.start_date} → {program.end_date || "?"}</span>}
            <span>· Topic: {program.training_topic}</span>
            <span>· By {program.created_by_name}</span>
          </div>
        </div>
        <Badge variant="outline" className={STATUS_COLORS[program.status === "closed" ? "completed" : "in_progress"]} data-testid="pd-status-badge">
          {program.status === "closed" ? "CLOSED" : "IN PROGRESS"}
        </Badge>
      </div>

      {/* Pipeline */}
      <Card className="mt-6 border-border">
        <CardContent className="p-6">
          <StepPipeline steps={program.steps} onStepClick={scrollToStep} />
          {currentStep && (
            <div className="mt-6 flex items-start justify-between gap-4 flex-wrap p-4 bg-primary/5 border border-primary/10 rounded-md">
              <div>
                <div className="text-[11px] uppercase tracking-widest text-primary">Currently active</div>
                <div className="font-heading font-semibold text-lg mt-1">Step {currentStep.step_number}: {currentStep.title}</div>
                <div className="text-sm text-muted-foreground mt-1 max-w-2xl">{currentStep.description}</div>
                <div className="flex items-center gap-3 text-xs mt-2 flex-wrap">
                  <Badge variant="outline" className={ROLE_COLORS[currentStep.owner]}>{ROLE_LABELS[currentStep.owner]}</Badge>
                  <span className="text-muted-foreground flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> Due {currentStep.due_date ? new Date(currentStep.due_date).toLocaleDateString() : "—"}</span>
                  {currentStep.status === "blocked" && <Badge variant="outline" className={STATUS_COLORS.blocked}>Blocked: {currentStep.blocked_reason}</Badge>}
                </div>
              </div>
              {canActCurrent && program.status !== "closed" && (
                <div className="flex items-center gap-2 flex-wrap">
                  {STEP_FIELDS[currentStep.step_number] && (
                    <Button data-testid="pd-open-form" variant="outline" onClick={() => setEditStep(currentStep.step_number)}>
                      Fill Details & Complete
                    </Button>
                  )}
                  {!STEP_FIELDS[currentStep.step_number] && (
                    <Button data-testid="pd-mark-complete" onClick={() => setEditStep(currentStep.step_number)}>
                      <CheckCircle2 className="w-4 h-4 mr-2" /> Mark Complete
                    </Button>
                  )}
                  {currentStep.step_number === 6 && !program.extension && (
                    <Button data-testid="pd-request-extension" variant="outline" onClick={() => setExtensionOpen(true)}>
                      Request Extension
                    </Button>
                  )}
                  {currentStep.status !== "blocked" ? (
                    <Button variant="ghost" size="sm" data-testid="pd-block-btn" onClick={() => setBlockStep(currentStep.step_number)}>
                      <AlertOctagon className="w-4 h-4 mr-1" /> Block
                    </Button>
                  ) : (
                    <Button variant="ghost" size="sm" data-testid="pd-unblock-btn" onClick={async () => {
                      await api.post(`/programs/${id}/steps/${currentStep.step_number}/unblock`);
                      await load();
                      toast.success("Step unblocked");
                    }}>
                      <Play className="w-4 h-4 mr-1" /> Unblock
                    </Button>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Extension banner */}
          {program.extension && (
            <div className={cn("mt-4 p-4 border rounded-md flex items-start justify-between gap-3 flex-wrap",
              program.extension.decision?.approved === true ? "bg-emerald-50 border-emerald-200" :
              program.extension.decision?.approved === false ? "bg-red-50 border-red-200" :
              "bg-amber-50 border-amber-200"
            )}>
              <div>
                <div className="text-[11px] uppercase tracking-widest">Extension Request</div>
                <div className="text-sm mt-1">
                  <span className="font-semibold">{program.extension.requested_days} days</span> requested by {program.extension.requested_by}. Reason: {program.extension.reason}
                </div>
                {program.extension.decision && (
                  <div className="text-xs mt-1">
                    {program.extension.decision.approved ? "Approved" : "Rejected"} by {program.extension.decision.decided_by}. {program.extension.decision.comment}
                  </div>
                )}
              </div>
              {!program.extension.decision && hasRole(user, "bd") && (
                <Button size="sm" data-testid="pd-decide-ext" onClick={() => setExtDecisionOpen(true)}>Decide</Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs value={tab} onValueChange={setTab} className="mt-8">
        <TabsList data-testid="pd-tabs">
          <TabsTrigger value="workflow" data-testid="tab-workflow">Workflow Details</TabsTrigger>
          <TabsTrigger value="emails" data-testid="tab-emails">Emails ({emails.length})</TabsTrigger>
          <TabsTrigger value="discussion" data-testid="tab-discussion">Discussion ({comments.length})</TabsTrigger>
          <TabsTrigger value="meta" data-testid="tab-meta">Overview</TabsTrigger>
        </TabsList>

        <TabsContent value="workflow" className="mt-6">
          <div className="space-y-6">
            {program.steps.map((s) => (
              <div key={s.step_number} id={`step-${s.step_number}`} className="space-y-3 scroll-mt-6">
                <StepCard
                  step={s}
                  isCurrent={s.step_number === program.current_step}
                  canEdit={hasRole(user, s.owner) && s.status !== "completed" && program.status !== "closed"}
                  canReopen={hasRole(user, s.owner) && s.status === "completed" && program.status !== "closed"}
                  onOpen={() => setEditStep(s.step_number)}
                  onReopen={() => setReopenStep(s.step_number)}
                />
                {/* Inline email compose for step 6 (Welcome, Delivery & Feedback) — welcome + feedback panels */}
                {s.step_number === 6 && s.status !== "pending" && (
                  <div className="ml-4 pl-4 border-l-2 border-primary/20 space-y-4">
                    <div>
                      <div className="text-[11px] uppercase tracking-widest text-muted-foreground mb-2 flex items-center gap-1"><Paperclip className="w-3 h-3" /> Attendance summary attachment</div>
                      <AttachmentsPanel
                        programId={id}
                        stepNumber={6}
                        canEdit={hasRole(user, "pm") && program.status !== "closed"}
                      />
                    </div>
                    <div>
                      <div className="text-[11px] uppercase tracking-widest text-muted-foreground mb-2">Welcome emails — Compose & send from trackly@nexusiqsolutions.com</div>
                      <EmailCompose
                        program={program}
                        stepNumber={6}
                        kinds={["welcome_batch", "welcome_faculty"]}
                        canSend={hasRole(user, "pm") && program.status !== "closed"}
                        onSent={load}
                      />
                    </div>
                    <div>
                      <div className="text-[11px] uppercase tracking-widest text-muted-foreground mb-2 flex items-center gap-1"><Users className="w-3 h-3" /> New members — add anytime during delivery; IT is notified to send invites</div>
                      <NewMembersPanel
                        programId={id}
                        canEdit={hasRole(user, "pm") && program.status !== "closed"}
                      />
                    </div>
                    <div>
                      <div className="text-[11px] uppercase tracking-widest text-muted-foreground mb-2">Feedback emails — Compose & send from trackly@nexusiqsolutions.com</div>
                      <EmailCompose
                        program={program}
                        stepNumber={6}
                        kinds={["feedback_training", "feedback_faculty"]}
                        canSend={hasRole(user, "pm") && program.status !== "closed"}
                        onSent={load}
                      />
                    </div>
                  </div>
                )}
                {/* Inline billing broadcast for step 7 */}
                {s.step_number === 7 && s.status !== "pending" && (
                  <div className="ml-4 pl-4 border-l-2 border-primary/20">
                    <div className="text-[11px] uppercase tracking-widest text-muted-foreground mb-2">Billing files & broadcast</div>
                    <BillingCompose
                      program={program}
                      stepNumber={7}
                      canSend={hasRole(user, "it") && program.status !== "closed"}
                      onSent={load}
                    />
                  </div>
                )}
                {/* Generic attachments for other in-progress/completed steps */}
                {![6, 7].includes(s.step_number) && (s.status === "in_progress" || s.status === "completed") && (
                  <div className="ml-4 pl-4 border-l-2 border-slate-200">
                    <div className="text-[11px] uppercase tracking-widest text-muted-foreground mb-2 flex items-center gap-1"><Paperclip className="w-3 h-3" /> Attachments</div>
                    <AttachmentsPanel
                      programId={id}
                      stepNumber={s.step_number}
                      canEdit={hasRole(user, s.owner) && s.status !== "completed" && program.status !== "closed"}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="emails" className="mt-6">
          <EmailPanel emails={emails} />
        </TabsContent>

        <TabsContent value="discussion" className="mt-6">
          <Discussion programId={id} comments={comments} onReload={load} />
        </TabsContent>

        <TabsContent value="meta" className="mt-6">
          <Overview program={program} />
        </TabsContent>
      </Tabs>

      {createPortal(
        <nav
          className="hidden xl:block fixed top-1/2 right-4 -translate-y-1/2 w-48 z-30 skeu-panel rounded-lg p-3 shadow-raised max-h-[80vh] overflow-y-auto"
          data-testid="step-jump-nav"
        >
          <div className="text-[11px] uppercase tracking-widest text-muted-foreground mb-2 px-1">Jump to Step</div>
          <div className="space-y-0.5">
            {program.steps.map((s) => {
              const meta = STEPS.find((m) => m.number === s.step_number);
              const isCurrent = s.step_number === program.current_step;
              return (
                <button
                  key={s.step_number}
                  type="button"
                  onClick={() => scrollToStep(s.step_number)}
                  data-testid={`step-jump-${s.step_number}`}
                  className={cn(
                    "w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-left text-xs transition-colors border",
                    isCurrent
                      ? "bg-primary/[0.08] text-primary border-primary/20 font-medium"
                      : "text-slate-600 border-transparent hover:bg-slate-100 hover:text-slate-900"
                  )}
                >
                  <span className={cn("w-2 h-2 rounded-full shrink-0", STATUS_DOT[s.status])} />
                  <span className="truncate">{s.step_number}. {meta?.short || s.title}</span>
                </button>
              );
            })}
          </div>
        </nav>,
        document.body
      )}

      {/* Dialogs */}
      <EditStepDialog
        open={editStep !== null}
        onClose={() => setEditStep(null)}
        programId={id}
        stepNumber={editStep}
        step={program.steps.find((s) => s.step_number === editStep) || null}
        program={program}
        isCurrent={editStep === program.current_step}
        onDone={load}
      />
      <BlockDialog open={blockStep !== null} onClose={() => setBlockStep(null)} programId={id} stepNumber={blockStep} onDone={load} />
      <ReopenDialog open={reopenStep !== null} onClose={() => setReopenStep(null)} programId={id} stepNumber={reopenStep} onDone={load} />
      <EmailDialog open={emailForStep !== null} onClose={() => setEmailForStep(null)} programId={id} preset={emailForStep} onDone={load} />
      <ExtensionRequestDialog open={extensionOpen} onClose={() => setExtensionOpen(false)} programId={id} onDone={load} />
      <ExtensionDecisionDialog open={extDecisionOpen} onClose={() => setExtDecisionOpen(false)} programId={id} onDone={load} />
    </div>
  );
}

function StepCard({ step, isCurrent, canEdit, canReopen, onOpen, onReopen }) {
  return (
    <Card data-testid={`step-card-${step.step_number}`} className={cn("border", isCurrent ? "border-primary/40 ring-1 ring-primary/10" : "border-border")}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-start gap-3 min-w-0">
            <div className={cn(
              "w-8 h-8 rounded-full grid place-items-center text-xs font-semibold border-2 shrink-0",
              step.status === "completed" ? "bg-emerald-600 text-white border-emerald-700" :
              step.status === "in_progress" ? "bg-blue-600 text-white border-blue-700" :
              step.status === "blocked" ? "bg-red-600 text-white border-red-700" :
              "bg-white text-slate-500 border-slate-300"
            )}>{step.step_number}</div>
            <div className="min-w-0">
              <div className="font-heading font-semibold">{step.title}</div>
              <div className="text-xs text-muted-foreground max-w-2xl">{step.description}</div>
              <div className="flex items-center gap-3 mt-2 text-xs flex-wrap">
                <Badge variant="outline" className={ROLE_COLORS[step.owner]}>{ROLE_LABELS[step.owner]}</Badge>
                <Badge variant="outline" className={STATUS_COLORS[step.status]}>{step.status.replace("_", " ").toUpperCase()}</Badge>
                {step.due_date && <span className="text-muted-foreground flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> Due {new Date(step.due_date).toLocaleDateString()}</span>}
                {step.completed_at && <span className="text-emerald-700 flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> Completed {new Date(step.completed_at).toLocaleDateString()}</span>}
              </div>
              {step.blocked_reason && <div className="text-xs text-red-700 mt-1">Blocked: {step.blocked_reason}</div>}
              {step.note && <div className="text-xs text-slate-600 mt-2 whitespace-pre-wrap"><span className="font-medium">Note:</span> {step.note}</div>}
              {step.form_data && Object.keys(step.form_data).length > 0 && (
                <div className="mt-3 grid gap-1 text-xs">
                  {Object.entries(step.form_data).map(([k, v]) => (
                    <div key={k} className="flex gap-2"><span className="text-muted-foreground min-w-[160px]">{k}</span><span className="text-slate-800">{TIME_FIELD_NAMES.has(k) ? formatTime12h(v) : String(v)}</span></div>
                  ))}
                </div>
              )}
            </div>
          </div>
          {canEdit && (
            <Button
              variant={isCurrent ? "default" : "outline"}
              size="sm"
              data-testid={`step-open-${step.step_number}`}
              onClick={onOpen}
            >
              {STEP_FIELDS[step.step_number] ? (isCurrent ? "Fill & Complete" : "Open / Save Draft") : (isCurrent ? "Mark Complete" : "Open")}
            </Button>
          )}
          {canReopen && (
            <Button
              variant="ghost"
              size="sm"
              data-testid={`step-reopen-${step.step_number}`}
              onClick={onReopen}
            >
              <RotateCcw className="w-4 h-4 mr-1" /> Reopen
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// Field types whose options come from an admin-managed catalog endpoint rather than
// a fixed list — shared by checkbox_group/user_multiselect/*_select field kinds.
const CATALOG_ENDPOINTS = {
  skills: "/skills",
  software: "/software-catalog",
  infra: "/infra-catalog",
  faculty: "/faculty",
};

// Program-level fields captured on the original Step 1 intake form (CreateProgram) —
// these live on the program document, not step 1's form_data, so they need their own
// section + payload key when step 1 is edited/reopened later.
const TIME_FIELD_NAMES = new Set(["time_slot_start", "time_slot_end", "delivery_time_start", "delivery_time_end"]);

const PROGRAM_FIELDS = [
  { name: "client_name", label: "Client Name", type: "text" },
  { name: "training_title", label: "Training Title", type: "text" },
  { name: "training_topic", label: "Training Topic / Domain", type: "text" },
  { name: "batch_size", label: "Batch Size", type: "number" },
  { name: "start_date", label: "Start Date", type: "date" },
  { name: "end_date", label: "End Date", type: "date" },
];

function EditStepDialog({ open, onClose, programId, stepNumber, step, program, isCurrent, onDone }) {
  const { user } = useAuth();
  const [note, setNote] = useState("");
  const [form, setForm] = useState({});
  const [programForm, setProgramForm] = useState({});
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState(null); // 'save' | 'complete'
  const [teamUsers, setTeamUsers] = useState([]);
  const [catalogOptions, setCatalogOptions] = useState({}); // { skills: [...], software: [...], infra: [...] }
  const fields = STEP_FIELDS[stepNumber] || [];

  useEffect(() => {
    if (open && step) {
      const initial = { ...(step.form_data || {}) };
      if (stepNumber === 4 && program) {
        const step2 = program.steps.find((s) => s.step_number === 2);
        if (!initial.delivery_time_start) initial.delivery_time_start = step2?.form_data?.time_slot_start || "";
        if (!initial.delivery_time_end) initial.delivery_time_end = step2?.form_data?.time_slot_end || "";
      }
      if (stepNumber === 6 && program) {
        const step2 = program.steps.find((s) => s.step_number === 2);
        const step4 = program.steps.find((s) => s.step_number === 4);
        if (!initial.training_start) initial.training_start = step4?.form_data?.actual_start_date || step2?.form_data?.infra_start_date || "";
        if (!initial.training_end) initial.training_end = step4?.form_data?.actual_end_date || step2?.form_data?.infra_end_date || "";
      }
      if (stepNumber === 7 && program && !initial.billing_amount) {
        const step3 = program.steps.find((s) => s.step_number === 3);
        if (step3?.form_data?.total_cost) initial.billing_amount = step3.form_data.total_cost;
      }
      setNote(step.note || "");
      setForm(initial);
    }
    if (open && stepNumber === 1 && program) {
      setProgramForm({
        client_name: program.client_name || "",
        training_title: program.training_title || "",
        training_topic: program.training_topic || "",
        batch_size: program.batch_size ?? 0,
        start_date: program.start_date || "",
        end_date: program.end_date || "",
      });
    }
  }, [open, step, stepNumber, program]);

  useEffect(() => {
    if (open && fields.some((f) => f.type === "user_select" || f.type === "user_multiselect")) {
      api.get("/users")
        .then(({ data }) => setTeamUsers(data.filter((u) => (u.roles || []).some((r) => !["client", "faculty"].includes(r)))))
        .catch(() => setTeamUsers([]));
    }
    const sources = [...new Set(fields.filter((f) => f.optionsSource).map((f) => f.optionsSource))];
    if (open && sources.length > 0) {
      Promise.all(sources.map((src) => api.get(CATALOG_ENDPOINTS[src]).then(({ data }) => [src, data]).catch(() => [src, []])))
        .then((pairs) => setCatalogOptions(Object.fromEntries(pairs)));
    }
  }, [open, stepNumber]);

  // Resolve the option list for a checkbox_group/radio/select-like field: either a
  // fixed f.options array, or admin-managed items fetched from f.optionsSource.
  const optionsFor = (f) => f.options || (catalogOptions[f.optionsSource] || []).map((it) => it.name);

  const toggleInArray = (name, value) => {
    const current = Array.isArray(form[name]) ? form[name] : [];
    const next = current.includes(value) ? current.filter((v) => v !== value) : [...current, value];
    setForm({ ...form, [name]: next });
  };

  const selectAll = (name, allValues) => {
    const current = Array.isArray(form[name]) ? form[name] : [];
    setForm({ ...form, [name]: current.length === allValues.length ? [] : allValues });
  };

  const save = async (confirmDurationMismatch = false) => {
    setLoading(true); setMode("save");
    try {
      const payload = { note, form_data: form, confirm_duration_mismatch: confirmDurationMismatch };
      if (stepNumber === 1) payload.program_fields = { ...programForm, batch_size: Number(programForm.batch_size) || 0 };
      await api.post(`/programs/${programId}/steps/${stepNumber}/save`, payload);
      toast.success(`Step ${stepNumber} draft saved`);
      await onDone(); onClose();
    } catch (e) {
      if (e.response?.status === 422 && typeof e.response?.data?.detail === "string") {
        toast.error(formatApiError(e.response?.data?.detail), {
          action: { label: "Save anyway", onClick: () => save(true) },
        });
      } else {
        toast.error(formatApiError(e.response?.data?.detail));
      }
    } finally { setLoading(false); setMode(null); }
  };

  const complete = async (confirmDurationMismatch = false) => {
    setLoading(true); setMode("complete");
    try {
      const payload = { note, form_data: form, confirm_duration_mismatch: confirmDurationMismatch };
      if (stepNumber === 1) payload.program_fields = { ...programForm, batch_size: Number(programForm.batch_size) || 0 };
      await api.post(`/programs/${programId}/steps/${stepNumber}/complete`, payload);
      toast.success(`Step ${stepNumber} completed`);
      await onDone(); onClose();
    } catch (e) {
      if (e.response?.status === 422 && typeof e.response?.data?.detail === "string") {
        toast.error(formatApiError(e.response?.data?.detail), {
          action: { label: "Save anyway", onClick: () => complete(true) },
        });
      } else {
        toast.error(formatApiError(e.response?.data?.detail));
      }
    } finally { setLoading(false); setMode(null); }
  };

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKeyDown);
    const prevBodyOverflow = document.body.style.overflow;
    const prevHtmlOverflow = document.documentElement.style.overflow;
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = prevBodyOverflow;
      document.documentElement.style.overflow = prevHtmlOverflow;
    };
  }, [open, onClose]);

  if (!open || !step) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 bg-background overflow-y-auto overscroll-contain page-enter" data-testid="step-fullpage">
      <div className="max-w-3xl mx-auto p-6 sm:p-10">
        <button onClick={onClose} className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1 mb-6" data-testid="step-fullpage-back">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to Program
        </button>
        {program && (
          <div className="mb-5 rounded-md border border-primary/20 bg-primary/5 px-4 py-3" data-testid="step-fullpage-program">
            <div className="text-[11px] uppercase tracking-widest text-muted-foreground">Program</div>
            <div className="font-heading text-lg font-semibold tracking-tight mt-0.5">{program.training_title}</div>
            <div className="text-xs text-muted-foreground mt-1 flex flex-wrap gap-x-4 gap-y-1">
              <span className="flex items-center gap-1"><Building2 className="w-3.5 h-3.5" /> {program.client_name || "—"}</span>
              {program.start_date && <span className="flex items-center gap-1"><CalIcon className="w-3.5 h-3.5" /> {program.start_date} → {program.end_date || "?"}</span>}
              {program.training_topic && <span>{program.training_topic}</span>}
            </div>
          </div>
        )}
        <div className="mb-6">
          <h2 className="font-heading text-2xl font-semibold tracking-tight">Step {stepNumber}: {step.title}</h2>
          <p className="text-sm text-muted-foreground mt-1">
            {isCurrent
              ? "This step is currently active. Fill in the details and mark it complete when done — the next team will be notified."
              : "You can save a draft of this step's details now. It can only be marked complete once the previous steps are done."}
          </p>
        </div>
        <div className="grid gap-4">
          {stepNumber === 1 && (
            <>
              <div className="text-[11px] uppercase tracking-widest text-muted-foreground">Program</div>
              {PROGRAM_FIELDS.map((f) => (
                <div className="space-y-2" key={f.name}>
                  <Label className="text-xs uppercase tracking-widest text-muted-foreground">{f.label}</Label>
                  <Input
                    data-testid={`form-program-${f.name}`}
                    type={f.type}
                    value={programForm[f.name] ?? ""}
                    onChange={(e) => setProgramForm({ ...programForm, [f.name]: e.target.value })}
                  />
                </div>
              ))}
              <div className="text-[11px] uppercase tracking-widest text-muted-foreground pt-2 border-t border-border">Sales Opportunity & Client Requirements (Step 1)</div>
            </>
          )}
          {stepNumber === 3 && program && (() => {
            const step2 = program.steps.find((s) => s.step_number === 2);
            const fd2 = step2?.form_data || {};
            const sw = Array.isArray(fd2.software_stack) ? fd2.software_stack.join(", ") : (fd2.software_stack || "—");
            return (
              <div className="text-xs bg-slate-50 border border-slate-200 rounded-md p-3 space-y-1" data-testid="step3-context-from-step2">
                <div className="text-[11px] uppercase tracking-widest text-muted-foreground mb-1">From Step 2 — Faculty Mapping & IT Handoff</div>
                <div><span className="text-muted-foreground">Infra Provisioning by NexusIQ:</span> {fd2.infra_provisioning_by_nexusiq || "Not answered yet — defaulting to Nexus"}</div>
                <div><span className="text-muted-foreground">Software / Frameworks Required:</span> {sw}</div>
                <div><span className="text-muted-foreground">Infra Start Date:</span> {fd2.infra_start_date || "—"} <span className="text-muted-foreground ml-3">Infra End Date:</span> {fd2.infra_end_date || "—"}</div>
                <div><span className="text-muted-foreground">Licensing part of this program:</span> {fd2.licensing_by_nexusiq || "Not answered yet — no licences"}</div>
                {isNexusLicensing(program) && <div><span className="text-muted-foreground">Licences Required:</span> {fd2.license_requirements || "—"}</div>}
                <div><span className="text-muted-foreground">Handoff Notes:</span> {fd2.handoff_notes || "—"}</div>
              </div>
            );
          })()}
          {stepNumber === 5 && program && (() => {
            const step2 = program.steps.find((s) => s.step_number === 2);
            const nexus = isNexusInfra(program);
            return (
              <div className="text-xs bg-slate-50 border border-slate-200 rounded-md p-3" data-testid="step5-context-from-step2">
                <div className="text-[11px] uppercase tracking-widest text-muted-foreground mb-1">From Step 2 — Faculty Mapping & IT Handoff</div>
                <div>
                  <span className="text-muted-foreground">Infra Provisioning by NexusIQ:</span>{" "}
                  {step2?.form_data?.infra_provisioning_by_nexusiq || "Not answered yet — defaulting to Nexus"}
                </div>
                {!nexus && <div className="text-muted-foreground mt-1">Client is providing their own infrastructure, so "Nexus Infra Used" is hidden below.</div>}
                <div className="mt-1">
                  <span className="text-muted-foreground">Licensing part of this program:</span>{" "}
                  {step2?.form_data?.licensing_by_nexusiq || "Not answered yet — no licences"}
                </div>
                {isNexusLicensing(program) && (() => {
                  const fd3 = program.steps.find((s) => s.step_number === 3)?.form_data || {};
                  return (
                    <>
                      <div><span className="text-muted-foreground">Licences Required:</span> {step2?.form_data?.license_requirements || "—"}</div>
                      <div><span className="text-muted-foreground">Licence Details (Step 3):</span> {fd3.license_details || "—"}{fd3.license_count ? ` · ${fd3.license_count} licence(s)` : ""}</div>
                      <div className="text-muted-foreground mt-1">Licence provisioning and testing fields are shown below.</div>
                    </>
                  );
                })()}
              </div>
            );
          })()}
          {stepNumber === 8 && program && (() => {
            const step2 = program.steps.find((s) => s.step_number === 2);
            const nexus = isNexusInfra(program);
            return (
              <div className="text-xs bg-slate-50 border border-slate-200 rounded-md p-3" data-testid="step8-context-from-step2">
                <div className="text-[11px] uppercase tracking-widest text-muted-foreground mb-1">From Step 2 — Faculty Mapping & IT Handoff</div>
                <div>
                  <span className="text-muted-foreground">Infra Provisioning by NexusIQ:</span>{" "}
                  {step2?.form_data?.infra_provisioning_by_nexusiq || "Not answered yet — defaulting to Nexus"}
                </div>
                <div className="text-muted-foreground mt-1">
                  {nexus ? "Nexus infra was used — PO Infra fields and Infra Closure Confirmation apply below." : "Client provided their own infrastructure — PO Infra fields are hidden below."}
                </div>
              </div>
            );
          })()}
          {stepNumber === 4 && step && (
            <div className="space-y-2" data-testid="step4-curriculum-attachment">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">
                Attach Document<span className="text-red-500"> *</span>
              </Label>
              <div className="text-xs text-muted-foreground -mt-1 mb-1">Approved curriculum file</div>
              <AttachmentsPanel
                programId={programId}
                stepNumber={4}
                canEdit={hasRole(user, step.owner) && step.status !== "completed" && program?.status !== "closed"}
              />
            </div>
          )}
          {stepNumber === 5 && step && (
            <div className="space-y-2" data-testid="step5-infra-attachment">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Attach Document</Label>
              <div className="text-xs text-muted-foreground -mt-1 mb-1">Infra provisioned details (lab access, credentials sheet, validation screenshots...)</div>
              <AttachmentsPanel
                programId={programId}
                stepNumber={5}
                canEdit={hasRole(user, step.owner) && step.status !== "completed" && program?.status !== "closed"}
              />
            </div>
          )}
          {stepNumber === 7 && step && (
            <div className="space-y-2" data-testid="step7-billing-attachment">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Attach Document</Label>
              <div className="text-xs text-muted-foreground -mt-1 mb-1">Infrastructure billing (invoice, screenshots, Excel...) — also selectable in the billing broadcast</div>
              <AttachmentsPanel
                programId={programId}
                stepNumber={7}
                canEdit={hasRole(user, step.owner) && step.status !== "completed" && program?.status !== "closed"}
              />
            </div>
          )}
          {stepNumber === 8 && step && (
            <div className="space-y-2" data-testid="step8-po-attachment">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Attach Document</Label>
              <div className="text-xs text-muted-foreground -mt-1 mb-1">Purchase Order copies (training / infra)</div>
              <AttachmentsPanel
                programId={programId}
                stepNumber={8}
                canEdit={hasRole(user, step.owner) && step.status !== "completed" && program?.status !== "closed"}
              />
            </div>
          )}
          {stepNumber === 9 && step && (
            <div className="space-y-2" data-testid="step9-invoice-attachment">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">
                Attach Document<span className="text-red-500"> *</span>
              </Label>
              <div className="text-xs text-muted-foreground -mt-1 mb-1">Invoice document — attaching it notifies BD</div>
              <AttachmentsPanel
                programId={programId}
                stepNumber={9}
                canEdit={hasRole(user, step.owner) && step.status !== "completed" && program?.status !== "closed"}
              />
            </div>
          )}
          {fields.length === 0 && (
            <div className="text-sm text-muted-foreground border border-dashed rounded-md p-4">
              This step has no structured fields — just add a completion note.
            </div>
          )}
          {fields.filter((f) => !f.showIf || f.showIf(form, program)).map((f) => (
            <React.Fragment key={f.name}>
            <div className="space-y-2">
              {f.type !== "checkbox" && (
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">
                  {f.label}{(f.required || (f.requiredIf && f.requiredIf(form, program))) && <span className="text-red-500"> *</span>}
                </Label>
              )}
              {f.type === "textarea" ? (
                <Textarea data-testid={`form-${f.name}`} rows={3} value={form[f.name] || ""} onChange={(e) => setForm({ ...form, [f.name]: e.target.value })} />
              ) : f.type === "checkbox" ? (
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    data-testid={`form-${f.name}`}
                    checked={!!form[f.name]}
                    onChange={(e) => setForm({ ...form, [f.name]: e.target.checked })}
                    className="h-4 w-4"
                  />
                  {f.label}
                </label>
              ) : f.type === "select" ? (
                <Select value={form[f.name] || ""} onValueChange={(v) => setForm({ ...form, [f.name]: v })}>
                  <SelectTrigger data-testid={`form-${f.name}`}><SelectValue placeholder="Select..." /></SelectTrigger>
                  <SelectContent>
                    {f.options.map((opt) => <SelectItem key={opt} value={opt}>{opt}</SelectItem>)}
                  </SelectContent>
                </Select>
              ) : f.type === "user_select" ? (
                <Select value={form[f.name] || ""} onValueChange={(v) => setForm({ ...form, [f.name]: v })}>
                  <SelectTrigger data-testid={`form-${f.name}`}><SelectValue placeholder="Select team member..." /></SelectTrigger>
                  <SelectContent>
                    {teamUsers.filter((u) => !f.roleFilter || (u.roles || []).includes(f.roleFilter)).map((u) => <SelectItem key={u.id} value={u.name}>{u.name} ({(u.roles || []).map((r) => ROLE_LABELS[r]).join(", ")})</SelectItem>)}
                  </SelectContent>
                </Select>
              ) : f.type === "infra_select" ? (
                <Select value={form[f.name] || ""} onValueChange={(v) => setForm({ ...form, [f.name]: v })}>
                  <SelectTrigger data-testid={`form-${f.name}`}><SelectValue placeholder="Select Nexus infra..." /></SelectTrigger>
                  <SelectContent>
                    {(catalogOptions.infra || []).length === 0 && (
                      <div className="px-2 py-1.5 text-xs text-muted-foreground">No infra in directory yet — add one under Admin &gt; Infra.</div>
                    )}
                    {(catalogOptions.infra || []).map((it) => (
                      <SelectItem key={it.id} value={it.name}>{it.name}{it.type ? ` (${it.type})` : ""}{it.specs ? ` — ${it.specs}` : ""}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : f.type === "faculty_multiselect" ? (
                <>
                  <MultiSelect
                    testId={`form-${f.name}`}
                    placeholder="Select from Faculty directory..."
                    options={(catalogOptions.faculty || []).map((it) => it.name)}
                    value={Array.isArray(form[f.name]) ? form[f.name] : []}
                    onChange={(next) => {
                      const picked = (catalogOptions.faculty || []).filter((it) => next.includes(it.name));
                      const skillIds = new Set(picked.flatMap((it) => it.expertise_skill_ids || []));
                      const skillNames = (catalogOptions.skills || []).filter((s) => skillIds.has(s.id)).map((s) => s.name);
                      setForm({
                        ...form,
                        [f.name]: next,
                        faculty_emails: picked.map((it) => it.email).filter(Boolean),
                        faculty_expertise: skillNames.length > 0 ? skillNames : (form.faculty_expertise || []),
                      });
                    }}
                  />
                  {(catalogOptions.faculty || []).length === 0 && (
                    <div className="text-xs text-muted-foreground">No faculty in directory yet — add one under Admin &gt; Faculty.</div>
                  )}
                </>
              ) : f.type === "radio" ? (
                <div className="flex flex-wrap gap-4" data-testid={`form-${f.name}`}>
                  {f.options.map((opt) => (
                    <label key={opt} className="flex items-center gap-2 text-sm cursor-pointer">
                      <input
                        type="radio"
                        name={`radio-${f.name}`}
                        checked={(form[f.name] || "") === opt}
                        onChange={() => setForm({ ...form, [f.name]: opt })}
                        className="h-4 w-4"
                      />
                      {opt}
                    </label>
                  ))}
                </div>
              ) : f.type === "computed" ? (
                <Input data-testid={`form-${f.name}`} disabled value={f.compute(form, program)} />
              ) : f.type === "checkbox_group" ? (
                <CheckboxGroup
                  testId={`form-${f.name}`}
                  options={optionsFor(f)}
                  value={Array.isArray(form[f.name]) ? form[f.name] : []}
                  onToggle={(opt) => toggleInArray(f.name, opt)}
                  onSelectAll={() => selectAll(f.name, optionsFor(f))}
                />
              ) : f.type === "multiselect_search" ? (
                <MultiSelect
                  testId={`form-${f.name}`}
                  options={optionsFor(f)}
                  value={Array.isArray(form[f.name]) ? form[f.name] : []}
                  onChange={(next) => setForm({ ...form, [f.name]: next })}
                />
              ) : f.type === "user_multiselect" ? (
                <CheckboxGroup
                  testId={`form-${f.name}`}
                  options={teamUsers.map((u) => u.name)}
                  value={Array.isArray(form[f.name]) ? form[f.name] : []}
                  onToggle={(opt) => toggleInArray(f.name, opt)}
                  onSelectAll={() => selectAll(f.name, teamUsers.map((u) => u.name))}
                />
              ) : f.type === "time" ? (
                <TimeInput testId={`form-${f.name}`} value={form[f.name] || ""} onChange={(v) => setForm({ ...form, [f.name]: v })} />
              ) : (
                <Input data-testid={`form-${f.name}`} type={f.type} value={form[f.name] || ""} onChange={(e) => setForm({ ...form, [f.name]: e.target.value })} />
              )}
              {stepNumber === 7 && f.name === "billing_amount" && program && (() => {
                const step3 = program.steps.find((s) => s.step_number === 3);
                const recommended = step3?.form_data?.total_cost;
                if (!recommended) return null;
                return (
                  <div className="text-xs text-muted-foreground flex items-center gap-2" data-testid="billing-amount-recommendation">
                    Recommended (Step 3 Total Cost): ₹{recommended}
                    {Number(form.billing_amount) !== Number(recommended) && (
                      <button
                        type="button"
                        className="text-primary hover:underline"
                        onClick={() => setForm({ ...form, billing_amount: recommended })}
                      >
                        Use this
                      </button>
                    )}
                  </div>
                );
              })()}
            </div>
            {stepNumber === 6 && f.name === "attendance_summary" && step && (
              <div className="space-y-2" data-testid="step6-attendance-attachment">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Attach Document</Label>
                <div className="text-xs text-muted-foreground -mt-1 mb-1">Attendance summary evidence (screenshots, sheet, PDF...)</div>
                <AttachmentsPanel
                  programId={programId}
                  stepNumber={6}
                  canEdit={hasRole(user, step.owner) && step.status !== "completed" && program?.status !== "closed"}
                />
              </div>
            )}
            {stepNumber === 4 && f.name === "batch_details" && step && (
              <div className="space-y-2" data-testid="step4-batch-details-attachment">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Attach Document</Label>
                <div className="text-xs text-muted-foreground -mt-1 mb-1">Batch roster / schedule file</div>
                <AttachmentsPanel
                  programId={programId}
                  stepNumber={4}
                  canEdit={hasRole(user, step.owner) && step.status !== "completed" && program?.status !== "closed"}
                />
              </div>
            )}
            </React.Fragment>
          ))}
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-widest text-muted-foreground">Note</Label>
            <Textarea data-testid="complete-note" rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Optional note..." />
          </div>
        </div>
        <div className="flex flex-wrap justify-end gap-2 mt-8 pt-4 border-t border-border sticky bottom-0 bg-background py-4">
          <Button variant="outline" onClick={onClose} disabled={loading}>Cancel</Button>
          <Button variant="secondary" data-testid="save-draft" onClick={() => save()} disabled={loading}>
            {loading && mode === "save" ? "Saving…" : "Save Draft"}
          </Button>
          {isCurrent && (
            <Button data-testid="complete-submit" onClick={() => complete()} disabled={loading}>
              <CheckCircle2 className="w-4 h-4 mr-2" />
              {loading && mode === "complete" ? "Completing…" : "Complete Step"}
            </Button>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

function BlockDialog({ open, onClose, programId, stepNumber, onDone }) {
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);
  useEffect(() => { if (open) setReason(""); }, [open]);

  const submit = async () => {
    setLoading(true);
    try {
      await api.post(`/programs/${programId}/steps/${stepNumber}/block`, { reason });
      toast.success("Step marked as blocked");
      await onDone(); onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Block Step {stepNumber}</DialogTitle>
          <DialogDescription>Describe what is blocking this step. Admin will be notified.</DialogDescription>
        </DialogHeader>
        <Textarea data-testid="block-reason" rows={3} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="What is blocking this step?" />
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button variant="destructive" data-testid="block-submit" onClick={submit} disabled={loading || !reason}>Block Step</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ReopenDialog({ open, onClose, programId, stepNumber, onDone }) {
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);
  useEffect(() => { if (open) setReason(""); }, [open]);

  const submit = async () => {
    setLoading(true);
    try {
      await api.post(`/programs/${programId}/steps/${stepNumber}/reopen`, { reason });
      toast.success(`Step ${stepNumber} reopened`);
      await onDone(); onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Send back to Step {stepNumber}</DialogTitle>
          <DialogDescription>The previous team will be notified and become the current owner.</DialogDescription>
        </DialogHeader>
        <Textarea data-testid="reopen-reason" rows={3} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Why are you reopening this step?" />
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid="reopen-submit" onClick={submit} disabled={loading || !reason}>Reopen Step</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EmailDialog({ open, onClose, programId, preset, onDone }) {
  const [kind, setKind] = useState("welcome_batch");
  const [recipients, setRecipients] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && preset) {
      setKind(preset.kind);
      const presets = {
        welcome_batch: { s: "Welcome to your NexusIQ training", b: "Dear participant,\n\nWelcome to the training program. Details of the schedule, access URLs and pre-reads will follow.\n\nBest,\nNexusIQ Program Management" },
        welcome_faculty: { s: "Faculty onboarding — Upcoming training", b: "Dear Faculty,\n\nThank you for partnering with NexusIQ. Please find the training details, batch composition and infra access below.\n\nBest,\nNexusIQ Program Management" },
        feedback_training: { s: "We'd love your feedback on the training", b: "Dear participant,\n\nThe training is now complete. Please share your feedback via the form link.\n\nBest,\nNexusIQ Program Management" },
        feedback_faculty: { s: "Faculty feedback request", b: "Dear participant,\n\nPlease share feedback about the faculty.\n\nBest,\nNexusIQ Program Management" },
        custom: { s: "", b: "" },
      };
      setSubject(presets[preset.kind]?.s || "");
      setBody(presets[preset.kind]?.b || "");
      setRecipients("");
    }
  }, [open, preset]);

  const submit = async () => {
    setLoading(true);
    try {
      const recs = recipients.split(/[,;\s]+/).filter(Boolean);
      await api.post(`/programs/${programId}/emails`, { kind, recipients: recs, subject, body });
      toast.success("Email queued (mocked). See Emails tab.");
      await onDone(); onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Compose & Trigger Email</DialogTitle>
          <DialogDescription>Trackly will log this email delivery for audit. (Mocked in this environment.)</DialogDescription>
        </DialogHeader>
        <div className="grid gap-4">
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-widest text-muted-foreground">Kind</Label>
            <Select value={kind} onValueChange={setKind}>
              <SelectTrigger data-testid="email-kind"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="welcome_batch">Welcome — Batch</SelectItem>
                <SelectItem value="welcome_faculty">Welcome — Faculty</SelectItem>
                <SelectItem value="feedback_training">Feedback — Training</SelectItem>
                <SelectItem value="feedback_faculty">Feedback — Faculty</SelectItem>
                <SelectItem value="custom">Custom</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-widest text-muted-foreground">Recipients (comma / space separated)</Label>
            <Textarea data-testid="email-recipients" rows={2} value={recipients} onChange={(e) => setRecipients(e.target.value)} placeholder="alice@client.com, bob@client.com" />
          </div>
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-widest text-muted-foreground">Subject</Label>
            <Input data-testid="email-subject" value={subject} onChange={(e) => setSubject(e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-widest text-muted-foreground">Body</Label>
            <Textarea data-testid="email-body" rows={8} value={body} onChange={(e) => setBody(e.target.value)} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid="email-send" onClick={submit} disabled={loading || !recipients || !subject}><Send className="w-4 h-4 mr-2" />Send</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ExtensionRequestDialog({ open, onClose, programId, onDone }) {
  const [days, setDays] = useState(7);
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);
  useEffect(() => { if (open) { setDays(7); setReason(""); } }, [open]);

  const submit = async () => {
    setLoading(true);
    try {
      await api.post(`/programs/${programId}/extension/request`, { requested_days: Number(days), reason });
      toast.success("Extension request sent to BD & Admin");
      await onDone(); onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Request Training Extension</DialogTitle>
          <DialogDescription>Extension requires approval before infra can be extended by IT.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-4">
          <div className="space-y-2">
            <Label>Additional Days</Label>
            <Input data-testid="ext-days" type="number" value={days} min={1} onChange={(e) => setDays(e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label>Reason</Label>
            <Textarea data-testid="ext-reason" rows={3} value={reason} onChange={(e) => setReason(e.target.value)} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid="ext-submit" onClick={submit} disabled={loading || !reason}>Request Extension</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ExtensionDecisionDialog({ open, onClose, programId, onDone }) {
  const [comment, setComment] = useState("");
  const [loading, setLoading] = useState(false);

  const decide = async (approved) => {
    setLoading(true);
    try {
      await api.post(`/programs/${programId}/extension/decide`, { approved, comment });
      toast.success(approved ? "Extension approved" : "Extension rejected");
      await onDone(); onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Decide Extension Request</DialogTitle>
          <DialogDescription>Approve or reject the extension. PM & IT will be notified.</DialogDescription>
        </DialogHeader>
        <Textarea data-testid="ext-decision-comment" rows={3} value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Comment (optional)" />
        <DialogFooter className="flex justify-between gap-2 flex-wrap">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <div className="flex gap-2">
            <Button variant="destructive" onClick={() => decide(false)} disabled={loading} data-testid="ext-reject">Reject</Button>
            <Button onClick={() => decide(true)} disabled={loading} data-testid="ext-approve">Approve</Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EmailPanel({ emails, onSend, canSend }) {
  return (
    <div className="space-y-4">
      {emails.length === 0 && <div className="text-sm text-muted-foreground">No emails triggered yet. Workflow step notifications and emails composed on Steps 6 and 7 will show up here.</div>}
      {emails.map((e) => (
        <Card key={e.id} className="border-border">
          <CardContent className="p-4">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <div className="text-xs uppercase tracking-widest text-muted-foreground">{e.kind.replace("_", " ")}</div>
                <div className="font-semibold mt-1">{e.subject}</div>
                <div className="text-xs text-muted-foreground mt-1">From: <span className="font-mono">{e.sender || "trackly@nexusiqsolutions.com"}</span></div>
                <div className="text-xs text-muted-foreground mt-0.5">To: {e.recipients.join(", ")}</div>
                {e.cc && e.cc.length > 0 && <div className="text-xs text-muted-foreground mt-0.5">CC: {e.cc.join(", ")}</div>}
                {e.attachments && e.attachments.length > 0 && (
                  <div className="text-xs text-muted-foreground mt-1 flex items-center gap-1"><Paperclip className="w-3 h-3" /> {e.attachments.length} attachment(s): {e.attachments.map((a) => a.filename).join(", ")}</div>
                )}
                {e.suppressed_recipients && e.suppressed_recipients.length > 0 && (
                  <div className="text-xs text-amber-700 mt-1" data-testid="email-suppressed-recipients">
                    Not delivered (address on SES suppression list): {e.suppressed_recipients.join(", ")}
                  </div>
                )}
                {e.delivery_status === "failed" && e.error && (
                  <div className="text-xs text-red-600 mt-1">Send failed: {e.error}</div>
                )}
              </div>
              <div className="text-xs text-muted-foreground text-right">
                {new Date(e.created_at).toLocaleString()} · by {e.triggered_by}
                {e.delivery_status && (
                  <div className={`mt-1 uppercase tracking-widest text-[10px] ${e.delivery_status === "sent" ? "text-emerald-700" : e.delivery_status === "failed" ? "text-red-600" : "text-muted-foreground"}`} data-testid="email-delivery-status">
                    {e.delivery_status}
                  </div>
                )}
              </div>
            </div>
            <pre className="text-xs mt-3 whitespace-pre-wrap font-mono bg-slate-50 p-3 rounded border border-slate-200">{e.body}</pre>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function Discussion({ programId, comments, onReload }) {
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);

  const post = async () => {
    if (!text.trim()) return;
    setLoading(true);
    try {
      await api.post(`/programs/${programId}/comments`, { text });
      setText("");
      onReload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <div className="grid gap-4">
      <div className="flex gap-2">
        <Textarea data-testid="comment-text" rows={2} value={text} onChange={(e) => setText(e.target.value)} placeholder="Add a comment for the team..." />
        <Button data-testid="comment-post" onClick={post} disabled={loading}><MessageSquare className="w-4 h-4 mr-1" />Post</Button>
      </div>
      <div className="space-y-2">
        {comments.length === 0 && <div className="text-sm text-muted-foreground">No comments yet.</div>}
        {comments.map((c) => (
          <div key={c.id} className="flex items-start gap-3 border border-border rounded-md p-3 bg-white">
            <Avatar className="h-8 w-8"><AvatarFallback className="text-[10px] bg-primary/10 text-primary">{c.author_name.split(" ").map(s => s[0]).slice(0,2).join("")}</AvatarFallback></Avatar>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap text-xs">
                <span className="font-semibold">{c.author_name}</span>
                <Badge variant="outline" className={ROLE_COLORS[c.author_role]}>{ROLE_LABELS[c.author_role]}</Badge>
                <span className="text-muted-foreground">{new Date(c.created_at).toLocaleString()}</span>
              </div>
              <div className="text-sm mt-1 whitespace-pre-wrap">{c.text}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Overview({ program }) {
  return (
    <div className="grid sm:grid-cols-2 gap-6">
      <Card className="border-border"><CardContent className="p-5">
        <div className="text-[11px] uppercase tracking-widest text-muted-foreground">Program</div>
        <div className="mt-2 grid gap-2 text-sm">
          <Row label="Client" value={program.client_name} />
          <Row label="Topic" value={program.training_topic} />
          <Row label="Batch Size" value={String(program.batch_size)} />
          <Row label="Start Date" value={program.start_date || "-"} />
          <Row label="End Date" value={program.end_date || "-"} />
          <Row label="Status" value={program.status.toUpperCase()} />
          <Row label="Created By" value={program.created_by_name} />
          <Row label="Created At" value={program.created_at ? new Date(program.created_at).toLocaleString() : "-"} />
          {program.closed_at && <Row label="Closed At" value={new Date(program.closed_at).toLocaleString()} />}
        </div>
      </CardContent></Card>
      <Card className="border-border"><CardContent className="p-5">
        <div className="text-[11px] uppercase tracking-widest text-muted-foreground">Notes</div>
        <div className="text-sm mt-2 whitespace-pre-wrap">{program.notes || "-"}</div>
      </CardContent></Card>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex gap-3">
      <div className="w-28 text-muted-foreground text-xs uppercase tracking-widest">{label}</div>
      <div className="flex-1">{value}</div>
    </div>
  );
}
